// Reusable "Scan → prefill" strip used inside +Add dialogs (Bills, Vendors,
// Warranties). Wraps a hidden <input type="file" capture="environment"> so
// mobile taps open the camera and desktop taps open the file picker.
//
// Usage:
//   <ScanStrip
//     testId="vendor-scan"
//     label="Snap a business card — we'll fill this out."
//     buttonIcon={<Camera size={14} />}
//     buttonLabel="Scan card"
//     endpoint="/vendors/scan-card"
//     confidence={scanConfidence}
//     onScanned={(data) => setForm({ ...form, ...data })}
//     onConfidence={(c) => setScanConfidence(c)}
//   />
//
// The parent owns confidence state so it can reset when the dialog closes.

import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Camera, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const CONFIDENCE_CLASSES = {
  high:   "bg-hearth-sage/25 text-hearth-sageDark",
  medium: "bg-hearth-amber/25 text-[#7A5320]",
  low:    "bg-hearth-coral/20 text-hearth-coral",
};

/**
 * @param {object} props
 * @param {string} props.testId          — prefix for data-testids (e.g. "vendor-scan")
 * @param {string} props.label           — copy shown next to the icon
 * @param {string} props.buttonLabel     — CTA button copy (default: "Scan")
 * @param {string} props.endpoint        — POST target under /api (e.g. "/vendors/scan-card")
 * @param {(data:object) => void} props.onScanned  — receives the whole response object
 * @param {(c:string|null) => void} [props.onConfidence]  — parent-controlled confidence state
 * @param {string|null} [props.confidence]  — current confidence chip value
 * @param {string} [props.successPrefix]  — customises the success toast, e.g. "Filled from your card"
 */
export default function ScanStrip({
  testId,
  label,
  buttonLabel = "Scan",
  endpoint,
  onScanned,
  onConfidence,
  confidence,
  successPrefix = "Filled from your photo",
}) {
  const inputRef = useRef(null);
  const [scanning, setScanning] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setScanning(true);
    onConfidence?.(null);
    const t = toast.loading("Reading your photo…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onScanned(data);
      onConfidence?.(data.confidence || null);
      toast.success(`${successPrefix} · confidence ${data.confidence || "medium"}.`, { id: t });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't read that image.", { id: t });
    } finally {
      setScanning(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div
      data-testid={`${testId}-strip`}
      className="mb-4 -mt-1 rounded-2xl bg-hearth-stone/50 border border-dashed border-hearth-line px-4 py-3"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-hearth-mute">
          <Sparkles size={14} className="text-hearth-clay" />
          <span>{label}</span>
        </div>
        <div className="flex items-center gap-2">
          {confidence && (
            <span
              data-testid={`${testId}-confidence`}
              className={
                "text-xs px-2 py-0.5 rounded-full "
                + (CONFIDENCE_CLASSES[confidence] || CONFIDENCE_CLASSES.medium)
              }
            >
              {confidence} confidence
            </span>
          )}
          <input
            ref={inputRef}
            data-testid={`${testId}-input`}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <Button
            data-testid={`${testId}-button`}
            onClick={() => inputRef.current?.click()}
            disabled={scanning}
            className="rounded-full h-9 px-4 bg-hearth-clay hover:bg-hearth-clayDark text-white text-sm"
            type="button"
          >
            {scanning ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Camera size={14} className="mr-1.5" />}
            {scanning ? "Reading…" : buttonLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
